import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import InactivityWarning from "./InactivityWarning";

const ProtectedRoute = ({ children }) => {
	const [isValidating, setIsValidating] = useState(true);
	const [isValid, setIsValid] = useState(false);
	const [showInactivityWarning, setShowInactivityWarning] = useState(false);
	const [countdown, setCountdown] = useState(60);
	const token = localStorage.getItem("parallextoken");

	// Refs for tracking
	const logoutTimeoutRef = useRef(null);
	const warningTimeoutRef = useRef(null);
	const countdownIntervalRef = useRef(null);
	const lastActivityRef = useRef(Date.now());

	// Configuration (30 minutes total, 5 minute warning)
	const TOTAL_INACTIVITY_TIME = 30 * 60 * 1000; // 30 minutes
	const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout
	const COUNTDOWN_SECONDS = 5 * 60; // 5 minutes in seconds
	const SESSION_CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds

	// Reset all timers
	const resetTimers = useCallback(() => {
		lastActivityRef.current = Date.now();

		// Clear existing timers
		if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
		if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
		if (countdownIntervalRef.current)
			clearInterval(countdownIntervalRef.current);

		// Hide warning if shown
		setShowInactivityWarning(false);
		setCountdown(COUNTDOWN_SECONDS);

		// Set warning timeout (25 minutes from now)
		warningTimeoutRef.current = setTimeout(() => {
			setShowInactivityWarning(true);
			setCountdown(COUNTDOWN_SECONDS);

			// Start countdown
			countdownIntervalRef.current = setInterval(() => {
				setCountdown((prev) => {
					if (prev <= 1) {
						clearInterval(countdownIntervalRef.current);
						handleLogout();
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}, TOTAL_INACTIVITY_TIME - WARNING_TIME);

		// Set logout timeout (30 minutes from now)
		logoutTimeoutRef.current = setTimeout(() => {
			handleLogout();
		}, TOTAL_INACTIVITY_TIME);
	}, [COUNTDOWN_SECONDS]);

	// Handle logout
	const handleLogout = useCallback(() => {
		localStorage.removeItem("parallextoken");
		setIsValid(false);
		setShowInactivityWarning(false);
		toast.error("You have been logged out due to inactivity");
	}, []);

	// Handle user activity - resets timers
	const handleUserActivity = useCallback(() => {
		resetTimers();
	}, [resetTimers]);

	// Session validation (only checks token, not inactivity)
	const validateSession = useCallback(async () => {
		if (!token) {
			setIsValid(false);
			setIsValidating(false);
			return;
		}

		try {
			const res = await axios.get(
				"https://parallexsms.approot.ng/validate_session.php",
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (res.data.valid) {
				setIsValid(true);
				// Reset timers on successful validation
				resetTimers();
			} else {
				setIsValid(false);
				localStorage.removeItem("parallextoken");

				if (res.data.sessionInvalidated) {
					toast.error(res.data.message || "Session invalidated");
				}
			}
		} catch (err) {
			console.error("Session validation error:", err);
			setIsValid(false);
			localStorage.removeItem("parallextoken");
		} finally {
			setIsValidating(false);
		}
	}, [token, resetTimers]);

	// Setup activity listeners
	useEffect(() => {
		if (!token || !isValid) return;

		// Listen for user activity
		const events = [
			"mousedown",
			"mousemove",
			"keydown",
			"scroll",
			"touchstart",
			"click",
		];

		const activityHandler = () => {
			handleUserActivity();
		};

		// Add event listeners
		events.forEach((event) => {
			window.addEventListener(event, activityHandler);
		});

		// Also track when user comes back to tab
		const visibilityHandler = () => {
			if (!document.hidden) {
				handleUserActivity();
			}
		};

		document.addEventListener("visibilitychange", visibilityHandler);

		// Initial timer setup
		resetTimers();

		// Regular session check
		const sessionCheckInterval = setInterval(
			validateSession,
			SESSION_CHECK_INTERVAL
		);

		// Cleanup
		return () => {
			events.forEach((event) => {
				window.removeEventListener(event, activityHandler);
			});
			document.removeEventListener("visibilitychange", visibilityHandler);

			if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
			if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
			if (countdownIntervalRef.current)
				clearInterval(countdownIntervalRef.current);
			clearInterval(sessionCheckInterval);
		};
	}, [token, isValid, handleUserActivity, resetTimers, validateSession]);

	// Initial validation
	useEffect(() => {
		validateSession();
	}, [validateSession]);

	if (isValidating) {
		return (
			<div className='h-screen w-full flex flex-col justify-center items-center bg-white'>
				<CircularProgress color='warning' />
				<Typography variant='body2' className='mt-4 text-gray-600'>
					Validating session...
				</Typography>
			</div>
		);
	}

	if (!token || !isValid) {
		return <Navigate to='/login' replace />;
	}

	return (
		<>
			<InactivityWarning
				open={showInactivityWarning}
				secondsRemaining={countdown}
				totalSeconds={COUNTDOWN_SECONDS}
				onStayLoggedIn={handleUserActivity}
				onLogout={handleLogout}
			/>
			{children}
		</>
	);
};

export default ProtectedRoute;
